import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { notification } from 'antd';
import { useCartHandlers } from 'hooks/cart/useCartHandlers';
import { useToggleCart } from 'hooks/cart/useToggleCart';
import { useUpdateCartData } from 'hooks/cart/useUpdateCartData';
import { useIsTerminalMode } from 'hooks/useIsTerminalMode';
import { usePrevious } from 'hooks/usePrevious';
import { useSearchParams } from 'hooks/useSearchParams';
import { CartProvider, CartStepEnum } from './providers/CartProvider';
import { CartCheckoutFail } from './steps/CartCheckoutFail';
import { CartCheckoutSuccess } from './steps/CartCheckoutSuccess';
import { CartExplore } from './steps/CartExplore';
import { CartPayment } from './steps/CartPayment';
import * as Styled from './styles';
import { useAppDispatch, useAppSelector } from 'appRedux/hooks';
import { placeOrder } from 'appRedux/restApi';
import { getWalletBalance } from 'appRedux/restApi/WalletBalance';
import { isMobile } from 'utils';
import { createHash } from 'utils/generic/create-hash';
import { iteratorUtil } from 'utils/generic/iterator.util';
import { EPaymentStatus } from 'constants/account/payment-method';
import { IS_DEFAULT_MODE } from 'constants/app/configs';
import { PlaceOrderParams } from 'interfaces';

const CartSteps = {
  [CartStepEnum.Explore]: CartExplore,
  [CartStepEnum.Payment]: CartPayment,
  [CartStepEnum.Success]: CartCheckoutSuccess,
  [CartStepEnum.Fail]: CartCheckoutFail
};

enum CartItemToggledType {
  Added = 'added',
  Removed = 'removed'
}

const iterator = iteratorUtil();

export const CartView: FC = () => {
  useUpdateCartData();
  const dispatch = useAppDispatch();
  const { handleToggleCart } = useToggleCart();
  const { handleEmptyCart } = useCartHandlers();
  const { paymentStatus } = useSearchParams();
  const { isTerminalMode } = useIsTerminalMode();
  const { isOpen, data } = useAppSelector(({ cart }) => cart);
  const previousData = usePrevious(data, data);
  const [cartStep, setCartStep] = useState(CartStepEnum.Explore);
  const [cartPrice, setCartPrice] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState('');
  const walletBalance = useAppSelector(
    ({ walletBalance }) => walletBalance.data
  );
  const { t } = useTranslation();
  const prevToggledItemHash = useRef(0);

  const CartStepView = CartSteps[cartStep];

  const handleAfterOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setCartPrice('');
      setCartStep(CartStepEnum.Explore);
    }
  }, []);

  const handleCheckout = useCallback(
    async (params?: Partial<PlaceOrderParams>) => {
      setCheckoutLoading(true);
      void placeOrder(undefined, params, (successMessage, error) => {
        setCheckoutLoading(false);
        if (successMessage) {
          if (IS_DEFAULT_MODE) {
            void dispatch(getWalletBalance(`${walletBalance?.currency}`));
          }
          handleEmptyCart();
          setCartStep(CartStepEnum.Success);
          return;
        }
        if (error) {
          setCheckoutErrorMessage(error);
          setCartStep(CartStepEnum.Fail);
        }
      });
    },
    [walletBalance, handleEmptyCart]
  );

  const goBack = useCallback(() => {
    if (cartStep === CartStepEnum.Explore) {
      handleToggleCart(false);
      return;
    }
    setCartStep(prevState => prevState - 1);
  }, [cartStep]);

  const openNotification = (
    name: string,
    imgSrc: string,
    type: CartItemToggledType
  ) => {
    const id = iterator.next().toString();
    notification.open({
      key: id,
      message: null,
      description: (
        <Styled.NotificationBody>
          <Styled.NotificationImage
            loading="lazy"
            preview={false}
            src={imgSrc}
          />
          <div>
            <Styled.NotificationTitle>
              {type === CartItemToggledType.Added
                ? t('cart.addedToCart')
                : t('cart.removedFromCart')}
            </Styled.NotificationTitle>
            <Styled.NotificationDescription>
              {type === CartItemToggledType.Added
                ? t('cart.addedNotificationDescription', { name })
                : t('cart.removedNotificationDescription', { name })}
              <Styled.NotificationAction
                onClick={() => {
                  notification.destroy();
                  handleToggleCart(true);
                }}
              >
                {t('cart.clickHere')}
              </Styled.NotificationAction>
            </Styled.NotificationDescription>
          </div>
        </Styled.NotificationBody>
      ),
      placement: 'bottomRight',
      style: {
        width: '444px',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0px 12px 16px 0px rgba(119, 110, 171, 0.08)',
        border: '1px solid var(--v3-black-2)'
      },
      closeIcon: (
        <Styled.NotificationCloseButton>
          <Styled.NotificationCloseIcon />
        </Styled.NotificationCloseButton>
      )
    });
  };

  const toggledItem = useMemo(() => {
    if (previousData) {
      const addedItem = data.find(
        item => !previousData.some(prevItem => prevItem.id === item.id)
      );
      const removedItem = previousData.find(
        prevItem => !data.some(item => item.id === prevItem.id)
      );

      return addedItem
        ? { item: addedItem, type: CartItemToggledType.Added }
        : removedItem
        ? { item: removedItem, type: CartItemToggledType.Removed }
        : undefined;
    }
  }, [data, previousData]);

  useEffect(() => {
    if (toggledItem) {
      /*
       * TODO: this functionality is implemented due to wrong behavior on
       * staging (triggering 2 times, from time to time) consider investigating
       * issue
       */
      const toggledItemHash = createHash(toggledItem || '');
      if (toggledItemHash === prevToggledItemHash.current) {
        return;
      }
      prevToggledItemHash.current = toggledItemHash;
      !isOpen &&
        openNotification(
          toggledItem.item.name,
          toggledItem.item.media?.small || '',
          toggledItem.type
        );
    }
  }, [toggledItem]);

  useLayoutEffect(() => {
    if (!paymentStatus) return;
    if (paymentStatus === EPaymentStatus.SUCCESS) {
      setCartStep(CartStepEnum.Success);
    } else if (paymentStatus === EPaymentStatus.FAILED) {
      setCartStep(CartStepEnum.Fail);
    }
  }, [paymentStatus]);

  return (
    <Styled.CartDrawer
      open={isOpen}
      afterOpenChange={handleAfterOpenChange}
      placement={isMobile() ? 'bottom' : 'right'}
      onClose={() => handleToggleCart(false)}
      closable={false}
      width={!isTerminalMode ? '36%' : '456px'}
      size="default"
    >
      <CartProvider
        value={{
          cartStep,
          setCartStep,
          goBack,
          cartPrice,
          setCartPrice,
          checkoutLoading,
          handleCheckout,
          checkoutErrorMessage
        }}
      >
        <Styled.CartWrapper>
          <CartStepView />
        </Styled.CartWrapper>
      </CartProvider>
    </Styled.CartDrawer>
  );
};
